import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Volume2, VolumeX, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useCurrency } from "../lib/currency";

function VideoCard({ listing, active, onClick }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!ref.current) return;
    if (active) ref.current.play().catch(() => {});
    else ref.current.pause();
  }, [active]);

  const { format } = useCurrency();

  return (
    <div className="dv-video-card group" onClick={onClick} data-testid={`video-card-${listing.id}`}>
      <video ref={ref} src={listing.video_url} muted={muted} loop playsInline poster={listing.image_url} preload="metadata" />
      <div className="overlay" />
      <div className="absolute top-3 right-3 z-10">
        <button onClick={(e) => { e.stopPropagation(); setMuted(!muted); }} className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center" aria-label="Toggle sound">
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
      </div>
      {!active && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center"><Play size={22} className="text-slate-900 ml-1" fill="currentColor" /></div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 z-10 text-white">
        <span className="dv-pill" style={{ background: "rgba(255,255,255,0.18)", color: "#fff" }}>{listing.industry} · {listing.country}</span>
        <h4 className="font-bold text-lg mt-2 leading-tight line-clamp-2">{listing.business_name}</h4>
        <div className="flex items-center justify-between mt-2 text-sm">
          <span className="font-semibold">{format(listing.listing_type === "investment" ? listing.investment_requirement : listing.asking_price)}</span>
          <Link to={`/listing/${listing.id}`} onClick={e => e.stopPropagation()} className="text-white/90 flex items-center gap-1 text-xs">View <ArrowRight size={12} /></Link>
        </div>
      </div>
    </div>
  );
}

export default function VideoCarousel({ listings = [] }) {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const items = listings.filter(l => l.video_url);

  const scrollBy = (dir) => {
    if (!trackRef.current) return;
    trackRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mt-20" data-testid="video-carousel">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <span className="dv-eyebrow">Pitch Reels</span>
          <h2 className="dv-display dv-h2 mt-2">Hear it from the founders.</h2>
        </div>
        <div className="hidden md:flex gap-2">
          <button onClick={() => scrollBy(-1)} className="w-11 h-11 rounded-full border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center justify-center" aria-label="Prev"><ChevronLeft size={18} /></button>
          <button onClick={() => scrollBy(1)} className="w-11 h-11 rounded-full border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center justify-center" aria-label="Next"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div ref={trackRef} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: "none" }}>
        {items.map((l, i) => (
          <div key={l.id} className="flex-shrink-0 w-[260px] snap-start">
            <VideoCard listing={l} active={active === i} onClick={() => setActive(i)} />
          </div>
        ))}
      </div>
    </section>
  );
}
